import React from 'react';
import { Link } from 'react-router-dom';
import { Bot, Shield, Building2, ArrowRight, CheckCircle, MessageSquare } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import GlossaryAutoLinker from '@/components/glossary/GlossaryAutoLinker';

const AssistantsHub: React.FC = () => {
  const features = [
    { icon: Bot, title: 'AI-Powered Guidance', description: 'Intelligent assistants trained on industry-specific knowledge' },
    { icon: Shield, title: 'Governance-First', description: 'Built with trust, transparency, and compliance at the core' },
    { icon: Building2, title: 'Industry Expertise', description: 'Tailored for accounting, legal, and professional services' },
  ];

  const benefits = [
    'Answer client questions 24/7 via governed AI',
    'Reduce repetitive support queries by 60%+',
    'One assistant per website, deployed across all pages',
    'Scale your advisory capacity without hiring',
    'Built-in compliance and governance guardrails',
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-navy via-brand-blue to-brand-teal py-20 lg:py-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-space">
                Advisory Assistants
              </h1>
              <GlossaryAutoLinker>
                <p className="text-xl md:text-2xl text-white/90 mb-8">
                  Intelligent AI assistants that extend your team's expertise, 
                  answer client questions, and maintain your professional standards.
                </p>
              </GlossaryAutoLinker>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/assistants/get-assistant">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Your Assistant
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/assistants/pricing">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    View Pricing
                  </Button>
                </Link>
                <Link to="/assistants/industries">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    <MessageSquare className="mr-2 h-5 w-5" />
                    Try an Assistant (Live Demo)
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
              Why Advisory Assistants?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-teal/10 rounded-full mb-6">
                    <feature.icon className="h-8 w-8 text-brand-teal" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-card-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                What Your Assistant Can Do
              </h2>
              <GlossaryAutoLinker>
                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 bg-card p-4 rounded-lg border border-border">
                      <CheckCircle className="h-6 w-6 text-brand-teal flex-shrink-0 mt-0.5" />
                      <span className="text-card-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </GlossaryAutoLinker>
            </div>
          </div>
        </section>

        {/* Navigation Cards */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
              Explore Advisory Assistants
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <Link to="/assistants/industries" className="group">
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:border-brand-teal transition-colors">
                  <Building2 className="h-8 w-8 text-brand-teal mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-card-foreground group-hover:text-brand-teal transition-colors">Industries</h3>
                  <p className="text-sm text-muted-foreground">Explore solutions by industry vertical</p>
                </div>
              </Link>
              <Link to="/assistants/governance" className="group">
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:border-brand-teal transition-colors">
                  <Shield className="h-8 w-8 text-brand-teal mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-card-foreground group-hover:text-brand-teal transition-colors">Governance</h3>
                  <p className="text-sm text-muted-foreground">Our trust charters and compliance framework</p>
                </div>
              </Link>
              <Link to="/assistants/case-examples" className="group">
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:border-brand-teal transition-colors">
                  <Bot className="h-8 w-8 text-brand-teal mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-card-foreground group-hover:text-brand-teal transition-colors">Case Examples</h3>
                  <p className="text-sm text-muted-foreground">See real-world implementations</p>
                </div>
              </Link>
              <Link to="/assistants/pricing" className="group">
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:border-brand-teal transition-colors">
                  <ArrowRight className="h-8 w-8 text-brand-teal mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-card-foreground group-hover:text-brand-teal transition-colors">Pricing</h3>
                  <p className="text-sm text-muted-foreground">Transparent, simple pricing plans</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Extend Your Team?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Get started with an Advisory Assistant tailored to your practice in as little as 14 days.
            </p>
            <Link to="/assistants/get-assistant">
              <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                Get Your Assistant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsHub;
